/**
 * Türkçe karakterleri İngilizce karakterlere çevirir ve camelCase formatına dönüştürür
 * Örnek: "Stüdyo Promptu" -> "StudioPrompt"
 * @param text Dönüştürülecek metin
 * @returns camelCase formatında, Türkçe karaktersiz string
 */
export const toCamelCaseCode = (text: string): string => {
  if (!text || text.trim().length === 0) {
    return '';
  }

  // Türkçe karakterleri değiştir
  text = replaceTurkishCharacters(text);

  // Özel karakterleri ve sayı olmayan karakterleri temizle (sadece harf, rakam ve boşluk kalsın)
  text = text.replace(/[^a-zA-Z0-9\s]/g, '');

  // Birden fazla boşluğu tek boşluğa indir
  text = text.replace(/\s+/g, ' ');

  // Trim yap
  text = text.trim();

  if (text.length === 0) {
    return '';
  }

  // Kelimelere ayır
  const words = text.split(' ').filter(word => word.length > 0);

  if (words.length === 0) {
    return '';
  }

  // camelCase formatına çevir
  let result = '';

  // İlk kelime küçük harfle
  result += words[0].toLowerCase();

  // Diğer kelimeler PascalCase (ilk harf büyük)
  for (let i = 1; i < words.length; i++) {
    if (words[i].length > 0) {
      result += words[i].charAt(0).toUpperCase() + words[i].slice(1).toLowerCase();
    }
  }

  return result;
};

/**
 * Türkçe karakterleri İngilizce eşdeğerlerine çevirir
 * @param text Dönüştürülecek metin
 * @returns Türkçe karaktersiz metin
 */
export const replaceTurkishCharacters = (text: string): string => {
  if (!text) {
    return text;
  }

  const turkishCharMap: Record<string, string> = {
    'ç': 'c', 'Ç': 'C',
    'ğ': 'g', 'Ğ': 'G',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ş': 's', 'Ş': 'S',
    'ü': 'u', 'Ü': 'U'
  };

  return text.split('').map(char => turkishCharMap[char] || char).join('');
};

/**
 * ASCII olmayan karakterleri temizler veya dönüştürür.
 * HTTP header gibi ASCII zorunluluğu olan yerlerde güvenli string oluşturur.
 * @param text Dönüştürülecek metin
 * @returns ASCII formatında metin
 */
export const toAsciiSafeString = (text: string): string => {
  if (!text) return '';
  
  // Önce Türkçe karakterleri dönüştür
  let safeText = replaceTurkishCharacters(text);
  
  // Geriye kalan ASCII olmayan karakterleri temizle (0-127 arası karakterler kalsın)
  // eslint-disable-next-line no-control-regex
  safeText = safeText.replace(/[^\x00-\x7F]/g, '');
  
  return safeText;
};

/**
 * Türkçe karakterleri İngilizce karakterlere çevirir ve küçük harfe dönüştürür.
 * Örnek: "ŞERMİN" -> "sermin", "İrem" -> "irem"
 * @param text Dönüştürülecek metin
 * @returns Küçük harfli ve Türkçe karaktersiz metin
 */
export const toEnglishLowerCase = (text: string): string => {
  if (!text) return '';
  
  const charMap: Record<string, string> = {
    'ç': 'c', 'Ç': 'c',
    'ğ': 'g', 'Ğ': 'g',
    'ı': 'i', 'İ': 'i', 'I': 'i',
    'ö': 'o', 'Ö': 'o',
    'ş': 's', 'Ş': 's',
    'ü': 'u', 'Ü': 'u'
  };

  return text.split('').map(char => charMap[char] || char.toLowerCase()).join('');
};

/**
 * Code'un geçerli olup olmadığını kontrol eder
 * - Sadece harf, rakam içerebilir
 * - Boşluk veya özel karakter içeremez
 * - Türkçe karakter içeremez
 * - İlk karakter küçük harf olmalı (camelCase)
 * @param code Kontrol edilecek code
 * @returns Geçerli ise true
 */
export const isValidCode = (code: string): boolean => {
  if (!code || code.trim().length === 0) {
    return false;
  }

  // Sadece harf ve rakam içermeli
  if (!/^[a-zA-Z0-9]+$/.test(code)) {
    return false;
  }

  // İlk karakter küçük harf olmalı (camelCase kuralı)
  if (!/^[a-z]/.test(code)) {
    return false;
  }

  // Türkçe karakter kontrolü
  if (containsTurkishCharacters(code)) {
    return false;
  }

  return true;
};

/**
 * String'in Türkçe karakter içerip içermediğini kontrol eder
 * @param text Kontrol edilecek metin
 * @returns Türkçe karakter içeriyorsa true
 */
export const containsTurkishCharacters = (text: string): boolean => {
  if (!text) {
    return false;
  }

  const turkishChars = ['ç', 'Ç', 'ğ', 'Ğ', 'ı', 'İ', 'ö', 'Ö', 'ş', 'Ş', 'ü', 'Ü'];
  return turkishChars.some(char => text.includes(char));
};

